import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { FileRow } from '../documents.types';

interface UseKbDocumentSelectionParams {
  /** Rows currently rendered (the visible page only). */
  pageRows: FileRow[];
  /** Total documents in the KB across every page (knowledgeBase.fileCount). */
  totalCount: number;
  /**
   * When this value changes the selection is cleared — pass the active search
   * query so a different result set never carries stale selection.
   */
  resetKey?: string;
}

export interface KbDocumentSelection {
  /** Explicitly selected ids while not in across-pages mode. */
  selectedIds: Set<string>;
  /**
   * Ids de-selected while {@link isAllAcrossPages} is active. Everything except
   * these — across every page, loaded or not — counts as selected.
   */
  excludedIds: Set<string>;
  /** True when the user chose "select all across every page". */
  isAllAcrossPages: boolean;
  /** Effective number of selected documents, for display and analytics. */
  selectedCount: number;
  hasSelection: boolean;

  /** A row is selectable only if its source asset can be downloaded. */
  isSelectable: (row: FileRow) => boolean;
  isRowSelected: (row: FileRow) => boolean;
  /** Toggle a single row; supports shift-click range select within the page. */
  onToggleRow: (index: number, event: MouseEvent) => void;

  /** Selectable rows on the current page. */
  pageSelectableCount: number;
  /** Header checkbox tri-state. */
  allPageSelected: boolean;
  somePageSelected: boolean;
  onToggleSelectAllPage: () => void;

  onSelectAllAcrossPages: () => void;

  clearSelection: () => void;
}

/** A row is downloadable (and thus selectable) when it has a source asset and isn't uploading. */
export function isFileRowSelectable(row: FileRow): boolean {
  return !!row.assetId && !row.isUploading;
}

/**
 * Multi-select for the KB documents table. Selection persists across pages via
 * an id set, plus a distinct "all across pages" tier for the bulk case where
 * unloaded pages can't be enumerated up front. The host owns pagination; this
 * hook only tracks what's chosen.
 *
 * In "all across pages" mode the selection is the whole KB minus an exclusion
 * set, so de-selecting a visible row records an exclusion and leaves every other
 * page (loaded or not) selected rather than collapsing to the current page.
 */
export function useKbDocumentSelection(params: UseKbDocumentSelectionParams): KbDocumentSelection {
  const { pageRows, totalCount, resetKey } = params;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [excludedIds, setExcludedIds] = useState<Set<string>>(() => new Set());
  const [isAllAcrossPages, setIsAllAcrossPages] = useState(false);

  // Shift-click anchor (page-local): last toggled index + its intent. In
  // across-pages mode `add` means the anchor click re-included a row and
  // `remove` means it excluded one, so a range extends the same direction.
  const lastIndexRef = useRef<number | null>(null);
  const lastModeRef = useRef<'add' | 'remove'>('add');

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setIsAllAcrossPages(false);
    lastIndexRef.current = null;
  }, []);

  // A new result set (search change) invalidates the current selection.
  useEffect(() => {
    clearSelection();
  }, [resetKey, clearSelection]);

  // Signature of the rows currently on screen; changes on page navigation, sort
  // or add/remove.
  const pageSignature = useMemo(() => pageRows.map((row) => row.id).join(','), [pageRows]);

  // The shift-click anchor is a page-local index, so it's meaningless once the
  // visible rows change — reset it to avoid a range spanning a previous page.
  useEffect(() => {
    lastIndexRef.current = null;
  }, [pageSignature]);

  const pageSelectableIds = useMemo(
    () => pageRows.filter(isFileRowSelectable).map((row) => row.id),
    [pageRows],
  );

  const isRowSelected = useCallback(
    (row: FileRow): boolean => {
      if (!isFileRowSelectable(row)) return false;
      if (isAllAcrossPages) return !excludedIds.has(row.id);
      return selectedIds.has(row.id);
    },
    [isAllAcrossPages, excludedIds, selectedIds],
  );

  const onToggleRow = useCallback(
    (index: number, event: MouseEvent) => {
      const row = pageRows[index];
      if (!isFileRowSelectable(row)) return;

      const isRange = event.shiftKey && lastIndexRef.current !== null;
      const applyRange = (set: Set<string>, add: boolean) => {
        const from = Math.min(lastIndexRef.current!, index);
        const to = Math.max(lastIndexRef.current!, index);
        for (let i = from; i <= to; i++) {
          const r = pageRows[i];
          if (!isFileRowSelectable(r)) continue;
          if (add) set.add(r.id);
          else set.delete(r.id);
        }
      };

      // Across-pages: keep the implicit "all" selection and record de-selections
      // as exclusions, so toggling one row never drops the pages we can't see.
      if (isAllAcrossPages) {
        const next = new Set(excludedIds);
        if (isRange) {
          // `remove` means the anchor excluded a row — extend the exclusion.
          applyRange(next, lastModeRef.current === 'remove');
          setExcludedIds(next);
          return;
        }
        const excluding = !next.has(row.id);
        if (excluding) next.add(row.id);
        else next.delete(row.id);
        setExcludedIds(next);
        lastIndexRef.current = index;
        lastModeRef.current = excluding ? 'remove' : 'add';
        return;
      }

      const next = new Set(selectedIds);
      if (isRange) {
        applyRange(next, lastModeRef.current === 'add');
        setSelectedIds(next);
        return;
      }
      const selecting = !next.has(row.id);
      if (selecting) next.add(row.id);
      else next.delete(row.id);
      setSelectedIds(next);
      lastIndexRef.current = index;
      lastModeRef.current = selecting ? 'add' : 'remove';
    },
    [pageRows, isAllAcrossPages, excludedIds, selectedIds],
  );

  const selectedSelectableOnPage = isAllAcrossPages
    ? pageSelectableIds.filter((id) => !excludedIds.has(id)).length
    : pageSelectableIds.filter((id) => selectedIds.has(id)).length;
  const allPageSelected =
    pageSelectableIds.length > 0 && selectedSelectableOnPage === pageSelectableIds.length;
  const somePageSelected = !allPageSelected && selectedSelectableOnPage > 0;

  const onToggleSelectAllPage = useCallback(() => {
    lastIndexRef.current = null;
    // In across-pages mode the header toggles only the visible page (exclude it
    // or fold it back in); clearing everything is the selection bar's job.
    if (isAllAcrossPages) {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        const pageFullySelected = pageSelectableIds.every((id) => !next.has(id));
        if (pageFullySelected) {
          for (const id of pageSelectableIds) next.add(id);
        } else {
          for (const id of pageSelectableIds) next.delete(id);
        }
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected =
        pageSelectableIds.length > 0 && pageSelectableIds.every((id) => next.has(id));
      if (allSelected) {
        for (const id of pageSelectableIds) next.delete(id);
      } else {
        for (const id of pageSelectableIds) next.add(id);
      }
      return next;
    });
  }, [isAllAcrossPages, pageSelectableIds]);

  const onSelectAllAcrossPages = useCallback(() => {
    lastIndexRef.current = null;
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setIsAllAcrossPages(true);
  }, []);

  const selectedCount = isAllAcrossPages
    ? Math.max(totalCount - excludedIds.size, 0)
    : selectedIds.size;

  return {
    selectedIds,
    excludedIds,
    isAllAcrossPages,
    selectedCount,
    hasSelection: selectedCount > 0,
    isSelectable: isFileRowSelectable,
    isRowSelected,
    onToggleRow,
    pageSelectableCount: pageSelectableIds.length,
    allPageSelected,
    somePageSelected,
    onToggleSelectAllPage,
    onSelectAllAcrossPages,
    clearSelection,
  };
}
